import React, { memo, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Card } from 'transfix-ui/components/Card'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import ShipmentLane from 'apps/fleet-planner/components/ShipmentLane'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { brandColors } from 'transfix-ui'
import formatCurrency from 'utils/formatCurrency'
import { SHIPMENT_OFFER_PARAM, TEST_IDS } from '../../constants'
import { IOfferFragment } from '../../graphql/OfferFragment'
import { getOfferTypeLabel } from '../../utils/getOfferTypeLabel'
import { isReOfferEligible } from '../../utils/isReOfferEligible'
import ShipmentOfferRowButtons from '../ShipmentOfferRowButtons'
import ShipmentOfferRowExpiration from '../ShipmentOfferRowExpiration'

const useShipmentOfferRowStyles = makeStyles(theme => ({
  card: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
    '&.noBorder': {
      borderRadius: 0,
      boxShadow: 'none',
      borderWidth: 0,
    },
    '&.clickable:hover': {
      backgroundColor: brandColors.coolGray1,
      cursor: 'pointer',
    },
  },
  row: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexWrap: 'wrap',
    },
  },
  shipmentId: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    colors: brandColors.coolGray5,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  rateAndType: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),

    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(0.5),
    },
  },
  leftContainer: {
    [theme.breakpoints.up('md')]: {
      flex: `0 0 200px`,
    },
    [theme.breakpoints.up('xl')]: {
      flex: `0 0 400px`,
    },
  },
  laneWrapper: {
    flexGrow: 1,
  },
  rightContainer: {
    [theme.breakpoints.up('md')]: {
      flex: `0 0 100%`,
    },
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
      justifyContent: 'flex-end',
      flex: `0 0 auto`,
    },
  },
  grayText: {
    color: brandColors.coolGray5,
  },
  buttons: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(2),

    [theme.breakpoints.up('lg')]: {
      paddingTop: 0,
      marginTop: 0,
    },

    '&::before': {
      content: '""',
      position: 'absolute',
      top: '0',
      left: '-16px',
      right: '-16px',
      height: '1px',
      background: brandColors.coolGray2,

      [theme.breakpoints.up('lg')]: {
        display: 'none',
      },
    },
  },
}))

export interface IShipmentOfferRowProps {
  /**
   * shows unavailable load layout
   * @default false
   */
  disabled?: boolean
  offeredShipment: IOfferFragment
  /**
   * @default false
   */
  showBorder?: boolean
  onDeclineSuccess?: () => void
}

const ShipmentOfferRow = memo<IShipmentOfferRowProps>(
  ({ disabled = false, offeredShipment, onDeclineSuccess, showBorder = false }) => {
    const styles = useShipmentOfferRowStyles()
    const { trackEvent } = useFMSAnalytics()
    const { isMobile } = useBreakpointContext()
    const history = useHistory()
    const location = useLocation()

    const { carrierTotalRate, marketplaceId, offerType, stops = [], offerExpiresAt, offeredState } =
      offeredShipment || {}

    const offerTypeLabel = getOfferTypeLabel(offerType)
    const isReOffer = isReOfferEligible({ offeredState })
    const isClickable = !isReOffer && !disabled

    const rowClickHandler = useCallback(() => {
      if (!isClickable) {
        return
      }

      if (location.pathname === '/home') {
        trackEvent('Offers', 'CLICKED_OFFER_ROW_HOMEPAGE', { shipmentId: marketplaceId })
        history.push(`${location.pathname}/${marketplaceId}/book?${SHIPMENT_OFFER_PARAM}=1`)
      } else {
        trackEvent('Offers', 'CLICKED_OFFER_ROW_OFFERS_PAGE', { shipmentId: marketplaceId })
        history.push(`${location.pathname}/${marketplaceId}?${SHIPMENT_OFFER_PARAM}=1`)
      }
    }, [history, isClickable, location.pathname, marketplaceId, trackEvent])

    return (
      <div role='button' tabIndex={0} aria-hidden='true' onClick={rowClickHandler}>
        <Card
          dataTest={TEST_IDS.shipmentOfferRow}
          className={`${styles.card}${showBorder ? '' : ' noBorder'}${
            isClickable ? ' clickable' : ''
          }`}>
          <div className={styles.row}>
            <div className={styles.leftContainer}>
              <Typography variant='caption' component='div' className={styles.shipmentId}>
                #TFX-{marketplaceId}
              </Typography>
              <div className={styles.rateAndType}>
                <Typography
                  variant='subtitle2'
                  component='div'
                  className={disabled ? styles.grayText : ''}>
                  {formatCurrency(carrierTotalRate)}
                </Typography>
                <Typography variant='caption' component='div' className={styles.grayText}>
                  {offerTypeLabel}
                </Typography>
              </div>
              {disabled && !isMobile && (
                <Typography variant='caption' component='div' className={styles.grayText}>
                  Load Unavailable
                </Typography>
              )}
              {!disabled && !isMobile && (
                <ShipmentOfferRowExpiration offerExpiresAt={offerExpiresAt} offerType={offerType} />
              )}
            </div>
            <div className={styles.laneWrapper}>
              <ShipmentLane stops={stops} disabled={disabled} />
            </div>
            {!disabled && (
              <div className={styles.rightContainer}>
                <div className={styles.buttons}>
                  <ShipmentOfferRowButtons
                    shipmentId={marketplaceId}
                    offeredState={offeredState}
                    onDeclineSuccess={onDeclineSuccess}
                  />
                  {isMobile && (
                    <ShipmentOfferRowExpiration
                      offerExpiresAt={offerExpiresAt}
                      offerType={offerType}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    )
  }
)

export default ShipmentOfferRow
